import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const hasShownRef = useRef(false);

  const content = {
    en: {
      title: "Wait! Don't Leave Yet",
      subtitle: "Get a FREE consultation for your project",
      description: "We'll analyze your needs and provide a custom solution proposal - absolutely free!",
      cta: "Get Free Consultation",
      dismiss: "No thanks, maybe later"
    },
    tr: {
      title: "Bekleyin! Henüz Gitmeyin",
      subtitle: "Projeniz için ÜCRETSİZ danışmanlık alın",
      description: "İhtiyaçlarınızı analiz edip size özel bir çözüm önerisi sunacağız - tamamen ücretsiz!",
      cta: "Ücretsiz Danışmanlık Al",
      dismiss: "Hayır teşekkürler, belki sonra"
    },
    es: {
      title: "¡Espera! No te vayas todavía",
      subtitle: "Obtén una consulta GRATIS para tu proyecto",
      description: "Analizaremos tus necesidades y te proporcionaremos una propuesta personalizada - ¡totalmente gratis!",
      cta: "Obtener Consulta Gratis",
      dismiss: "No gracias, tal vez después"
    }
  };

  const t = content[language] || content['en'];

  useEffect(() => {
    // Check if already shown in this browser session
    const alreadyShown = sessionStorage.getItem('exitIntentShown') === 'true';
    if (alreadyShown || hasShownRef.current) {
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of page AND not shown before
      if (e.clientY <= 0 && !hasShownRef.current && sessionStorage.getItem('exitIntentShown') !== 'true') {
        hasShownRef.current = true;
        sessionStorage.setItem('exitIntentShown', 'true');
        setIsVisible(true);
      }
    };

    // Add delay before enabling exit intent (10 seconds)
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 10000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    hasShownRef.current = true;
    sessionStorage.setItem('exitIntentShown', 'true');
  };

  const handleCTA = () => {
    setIsVisible(false);
    hasShownRef.current = true;
    sessionStorage.setItem('exitIntentShown', 'true');
    window.location.href = '/contact';
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
            onClick={handleClose}
          />

          {/* Popup - Centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <div 
              className="bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-white/10 rounded-2xl p-8 relative overflow-hidden w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Glow effect */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-[#38BDF8]/20 blur-3xl" />
              
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>

              {/* Content */}
              <div className="relative z-10 text-center">
                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#38BDF8]/20 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-[#38BDF8]" />
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  {t.title}
                </h2>
                
                <p className="text-[#38BDF8] font-medium mb-3">
                  {t.subtitle}
                </p>
                
                <p className="text-white/60 text-sm mb-6">
                  {t.description}
                </p>

                {/* CTA Button */}
                <button
                  onClick={handleCTA}
                  className="w-full py-3 px-6 bg-[#38BDF8] hover:bg-[#38BDF8]/90 text-black font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.4)] mb-3"
                >
                  {t.cta}
                  <ArrowRight size={18} />
                </button>

                {/* Dismiss link */}
                <button
                  onClick={handleClose}
                  className="text-white/40 text-sm hover:text-white/60 transition-colors"
                >
                  {t.dismiss}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
